// useScrollProgress — tracks how far the user has scrolled down the
// current page as a number between 0 and 1.
//
// Uses requestAnimationFrame to throttle updates so we never do more
// than one write per frame, keeping the reading-progress bar smooth
// on long pages without spamming React re-renders.
//
// Falls back to 0 when the document isn't tall enough to scroll (e.g.
// admin routes, short landing screens) — that keeps the bar hidden by
// design instead of pinning to 100% on trivial pages.

import { useEffect, useState } from "react";

export default function useScrollProgress() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let frame = 0;

    const compute = () => {
      frame = 0;
      const doc = document.documentElement;
      const body = document.body;
      const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
      const scrollHeight = Math.max(
        doc.scrollHeight,
        body.scrollHeight,
        doc.offsetHeight,
        body.offsetHeight,
      );
      const clientHeight = window.innerHeight || doc.clientHeight || 0;
      const scrollable = Math.max(scrollHeight - clientHeight, 0);
      if (scrollable <= 0) {
        setProgress(0);
        return;
      }
      const ratio = Math.min(Math.max(scrollTop / scrollable, 0), 1);
      setProgress(ratio);
    };

    const onScrollOrResize = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, []);

  return progress;
}
